/**
 * Role utility functions for consistent permission checking
 * Handles both legacy (lowercase) and new (capitalized) role names
 */

// Admin roles (both legacy and new)
const ADMIN_ROLES = ['Admin', 'admin'];

// Super Admin roles (platform-level)
const SUPER_ADMIN_ROLES = ['Super Admin', 'super_admin'];

// HSE Manager roles (both legacy and new)
const HSE_MANAGER_ROLES = ['HSE Manager', 'hse_manager'];

// Safety Manager roles. Both spellings are needed: users.role stores the legacy
// title-case value while RBAC stores the slug, and checking only one form made
// Safety Managers fail role checks depending on how their account was created.
// Note: the RBAC-era equivalent slug is 'safety_officer' (see seed_rbac
// ROLE_MAPPING), covered separately by SAFETY_OFFICER_ROLES — it is deliberately
// not merged here, since SAFETY_MANAGER_ROLES feeds MANAGEMENT_ROLES.
const SAFETY_MANAGER_ROLES = ['Safety Manager', 'safety_manager'];

// Safety Officer roles
const SAFETY_OFFICER_ROLES = ['Safety Officer', 'safety_officer'];

// Roles authorized to edit/cancel a Safety Meeting (in addition to the meeting organizer)
const MEETING_MANAGE_ROLES = [
  ...ADMIN_ROLES,
  ...SUPER_ADMIN_ROLES,
  ...HSE_MANAGER_ROLES,
  ...SAFETY_OFFICER_ROLES,
];

// All management roles
const MANAGEMENT_ROLES = [...ADMIN_ROLES, ...HSE_MANAGER_ROLES, ...SAFETY_MANAGER_ROLES, 'Manager', 'Director'];

/**
 * Check if user has admin role
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  return user?.role && ADMIN_ROLES.includes(user.role);
};

/**
 * Check if user has HSE Manager role
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isHSEManager = (user) => {
  return user?.role && HSE_MANAGER_ROLES.includes(user.role);
};

/**
 * Check if user has Safety Manager role
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isSafetyManager = (user) => {
  return user?.role && SAFETY_MANAGER_ROLES.includes(user.role);
};

/**
 * Check if user has admin or HSE Manager role
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isAdminOrHSEManager = (user) => {
  return isAdmin(user) || isHSEManager(user);
};

/**
 * Check if user has any management role (admin, HSE manager, safety manager, etc.)
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const isManagement = (user) => {
  return user?.role && MANAGEMENT_ROLES.includes(user.role);
};

/**
 * Check if user can approve documents/workflows
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const canApprove = (user) => {
  return isAdmin(user) || isHSEManager(user) || isSafetyManager(user);
};

/**
 * Check if user can manage users
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const canManageUsers = (user) => {
  return isAdmin(user);
};

/**
 * Check if user can access admin settings
 * @param {object} user - User object with role property
 * @returns {boolean}
 */
export const canAccessAdminSettings = (user) => {
  return isAdmin(user) || isHSEManager(user);
};

/**
 * Check if user may pick an assignee ("Assign To") while reporting an
 * incident or observation: Admin, HSE Manager and Safety Officer. The picker
 * lists only the chosen department's Safety Managers (server-side), so this
 * gates convenience, not access — any reporter's record is still auto-routed.
 */
export const canAssignOnReport = (user) => {
  return Boolean(isAdmin(user) || isHSEManager(user)
    || (user?.role && SAFETY_OFFICER_ROLES.includes(user.role)));
};

/**
 * Check if user is authorized to edit or cancel a Safety Meeting.
 * Authorized = meeting organizer, OR a management role
 * (Admin, Super Admin, HSE Manager, Safety Officer).
 */
export const canManageMeeting = (user, meeting) => {
  if (!user || !meeting) return false;
  const userId = user.user_id || user.id;
  if (meeting.organizer_id && userId && userId === meeting.organizer_id) return true;
  return MEETING_MANAGE_ROLES.includes(user.role);
};

// Acronyms kept fully upper-cased when humanizing a role. Keep in sync with the
// backend `_humanize_role` in routes/ptw.py so the app and PDF read the same.
const ROLE_ACRONYMS = new Set(['HSE', 'EHS', 'PTW', 'QA', 'QC', 'HR', 'IT', 'ESI', 'WC', 'LOTO', 'CEO', 'CFO', 'COO', 'MD', 'GM']);

/**
 * Convert a snake_case or camelCase role name into a human-readable label.
 * e.g. "tenant_admin" → "Tenant Admin", "hse_manager" → "HSE Manager",
 *      "safety_officer" → "Safety Officer". Known acronyms are upper-cased.
 * Null/undefined returns ''.
 */
export const formatRoleName = (role) => {
  if (!role) return '';
  return String(role)
    .replace(/([a-z])([A-Z])/g, '$1 $2')  // camelCase → spaced
    .replace(/[_-]+/g, ' ')               // snake_case / kebab-case → spaced
    .trim()
    .split(/\s+/)
    .map((w) =>
      ROLE_ACRONYMS.has(w.toUpperCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join(' ');
};

export default {
  isAdmin,
  isHSEManager,
  isSafetyManager,
  isAdminOrHSEManager,
  isManagement,
  canApprove,
  canManageUsers,
  canAccessAdminSettings,
  canAssignOnReport,
  canManageMeeting,
  formatRoleName,
  ADMIN_ROLES,
  SUPER_ADMIN_ROLES,
  HSE_MANAGER_ROLES,
  SAFETY_MANAGER_ROLES,
  SAFETY_OFFICER_ROLES,
  MEETING_MANAGE_ROLES,
  MANAGEMENT_ROLES
};
